import { SalesFunnelReadable } from '@crm/api'
import { LeadCreateData } from '@crm/features/leads/utils'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { ConditionalText, DayLabel } from '@shared/components'
import { getFullName } from '@shared/utils'

import { Button, Card, Col, Flex, Row, theme, Tooltip } from 'antd'
import {
  Eye,
  Maximize4,
  Messages3,
  Personalcard,
  TagUser,
  TaskSquare,
  User
} from 'iconsax-react'
import { memo, PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'

import type { CardProps } from 'antd'

type Props = {
  data?: SalesFunnelReadable
  onActionClick?: (actionData: LeadCreateData) => void
} & CardProps

const KanbanCardComponent = ({ data, onActionClick, ...props }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()

  const { token } = theme.useToken()

  const navigate = useAppNavigate()

  const onClickPreview = () =>
    navigate(paths.leadsDetails.root(String(data?.id)))

  const onClickTasks = () =>
    navigate(paths.leadsDetails.tasks(String(data?.id)))

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) =>
    event.stopPropagation()

  return (
    <Card className={styles.card} {...props}>
      <Flex vertical gap={4} className={styles.cardContent}>
        <Row align="middle" gutter={4}>
          <Col span={21}>
            <Tooltip
              title={t('contacts.lead')}
              trigger="hover"
              placement="left"
            >
              <Flex align="center" gap={8}>
                <Flex>
                  <Personalcard size={16} color={token.colorIcon} />
                </Flex>

                <ConditionalText
                  condition={!!data?.name}
                  isEllipsis
                  weight={500}
                >
                  {data?.name}
                </ConditionalText>
              </Flex>
            </Tooltip>
          </Col>

          <Col span={3}>
            <Tooltip title={t('edit_lead')} trigger="hover">
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  onActionClick?.({
                    open: true,
                    type: 'edit',
                    leadId: String(data?.id)
                  })
                }}
                className={styles.expandButton}
                icon={<Maximize4 size={16} color={token.colorIcon} />}
                type="text"
                size="small"
              />
            </Tooltip>
          </Col>
        </Row>

        <Tooltip title={t('contact')} trigger="hover" placement="left">
          <Flex align="center" gap={8}>
            <Flex>
              <TagUser size={16} color={token.colorIcon} />
            </Flex>

            <ConditionalText
              condition={!!getFullName(data?.contact)}
              isEllipsis
              weight={500}
            >
              {getFullName(data?.contact)}
            </ConditionalText>
          </Flex>
        </Tooltip>

        <Tooltip title={t('task.owner')} trigger="hover" placement="left">
          <Flex align="center" gap={8}>
            <Flex>
              <User size={16} color={token.colorIcon} />
            </Flex>

            <ConditionalText
              condition={!!getFullName(data?.owner)}
              isEllipsis
              weight={500}
            >
              {getFullName(data?.owner)}
            </ConditionalText>
          </Flex>
        </Tooltip>
      </Flex>

      <Flex
        justify="space-between"
        align="center"
        gap={8}
        onPointerDown={onPointerDown}
      >
        <Flex align="center">
          <Tooltip title={t('preview')} trigger="hover">
            <Button
              icon={<Eye size={16} />}
              type="text"
              size="small"
              onClick={onClickPreview}
            />
          </Tooltip>

          <Tooltip title={t('tasks')} trigger="hover">
            <Button
              icon={<TaskSquare size={16} />}
              type="text"
              size="small"
              onClick={onClickTasks}
            />
          </Tooltip>

          <Tooltip title={t('contacts.comment')} trigger="hover">
            <Button
              icon={<Messages3 size={16} />}
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onActionClick?.({
                  open: true,
                  type: 'comments',
                  leadId: String(data?.id)
                })
              }}
            />
          </Tooltip>
        </Flex>

        <Tooltip title={t('modified_at')} trigger="hover">
          <DayLabel date={data?.modified_at} />
        </Tooltip>
      </Flex>
    </Card>
  )
}

export const LeadKanbanCard = memo(KanbanCardComponent)
